import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  FavoriteImage,
  FavoriteTag,
} from '@remodeling/content/src/model/vo/favorite-test';
import { MAX_COLOR, MAX_IMAGE, MAX_KEYWORD, MAX_MATERIAL } from '@/constants';

import type { RootState } from '../store';
import { findIndex } from 'lodash-es';

// Define a type for the slice state

export interface favoriteTestState {
  currentStage: string;
  currentImage: FavoriteImage[];
  currentColor: FavoriteTag[];
  currentKeyword: FavoriteTag[];
  currentMaterial: FavoriteTag[];
}

// Define the initial state using that type
const initialState: favoriteTestState = {
  currentStage: 'START',
  currentImage: [],
  currentColor: [],
  currentKeyword: [],
  currentMaterial: [],
};

export const favoriteTestSlice = createSlice({
  name: 'favoriteTest',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setCurrentStage: (state, action: PayloadAction<string>) => {
      state.currentStage = action.payload;
    },

    selectImage: (state, action: PayloadAction<FavoriteImage[]>) => {
      if (action.payload?.length === 0) {
        state.currentImage = [];
        return;
      }
      const itemId = action.payload?.[0]?.id;
      const found = findIndex(state.currentImage, { id: itemId });

      if (found >= 0) {
        state.currentImage.splice(found, 1);
      } else {
        if (state.currentImage.length < MAX_IMAGE) {
          state.currentImage.push(...action.payload);
        }
      }
    },
    selectColor: (state, action: PayloadAction<FavoriteTag[]>) => {
      if (action.payload?.length === 0) {
        state.currentColor = [];
        return;
      }
      const itemId = action.payload?.[0]?.id;
      const found = findIndex(state.currentColor, { id: itemId });

      if (found >= 0) {
        state.currentColor.splice(found, 1);
      } else {
        if (state.currentColor.length < MAX_COLOR) {
          state.currentColor.push(...action.payload);
        }
      }
    },
    selectKeyword: (state, action: PayloadAction<FavoriteTag[]>) => {
      if (action.payload?.length === 0) {
        state.currentKeyword = [];
        return;
      }
      const itemId = action.payload?.[0]?.id;
      const found = findIndex(state.currentKeyword, { id: itemId });

      if (found >= 0) {
        state.currentKeyword.splice(found, 1);
      } else {
        if (state.currentKeyword.length < MAX_KEYWORD) {
          state.currentKeyword.push(...action.payload);
        }
      }
    },
    selectMaterial: (state, action: PayloadAction<FavoriteTag[]>) => {
      if (action.payload?.length === 0) {
        state.currentMaterial = [];
        return;
      }
      const itemId = action.payload?.[0]?.id;
      const found = findIndex(state.currentMaterial, { id: itemId });

      if (found >= 0) {
        state.currentMaterial.splice(found, 1);
      } else {
        if (state.currentMaterial.length < MAX_MATERIAL) {
          state.currentMaterial.push(...action.payload);
        }
      }
    },
  },
});

export const {
  setCurrentStage,
  selectImage,
  selectColor,
  selectKeyword,
  selectMaterial,
} = favoriteTestSlice.actions;

// Other code such as selectors can use the imported `RootState` type

export const selectSetCurrentStage = (state: RootState) =>
  state.favoriteTest.currentStage;
export const selectCurrentImage = (state: RootState) =>
  state.favoriteTest.currentImage;
export const selectCurrentColor = (state: RootState) =>
  state.favoriteTest.currentColor;
export const selectCurrentKeyword = (state: RootState) =>
  state.favoriteTest.currentKeyword;
export const selectCurrentMaterial = (state: RootState) =>
  state.favoriteTest.currentMaterial;

export default favoriteTestSlice;
